/**
 * @file Contributor UI — backgroundDataReviewers. Data Review dropdown slugs (see seedUserAnalysisSeeder). */

export const DATA_REVIEW_ONE = "one_external_reviewer";
/** Exported const — DATA REVIEW TWO. */
export const DATA_REVIEW_TWO = "two_external_reviewers";
/** Exported const — DATA REVIEW PANEL. */
export const DATA_REVIEW_PANEL = "external_panel_review";

/** Canonical display labels ↔ internal keys for external reviewer options. */
export const DATA_REVIEW_EXTERNAL_OPTIONS = [
  {
    key: DATA_REVIEW_PANEL,
    label: "External panel review (3 or more people)",
    reviewerCount: 3,
  },
  {
    key: DATA_REVIEW_TWO,
    label: "2 External reviewers",
    reviewerCount: 2,
  },
  {
    key: DATA_REVIEW_ONE,
    label: "1 external reviewer",
    reviewerCount: 1,
  },
] as const;

const DATA_REVIEW_ALL_KEYS = new Set([
  DATA_REVIEW_ONE,
  DATA_REVIEW_TWO,
  DATA_REVIEW_PANEL,
  "internal_review",
  "no_review_stated",
]);

const DATA_REVIEW_LABEL_TO_KEY = new Map<string, string>(
  DATA_REVIEW_EXTERNAL_OPTIONS.flatMap((opt) => [
    [opt.key.toLowerCase(), opt.key],
    [opt.label.toLowerCase(), opt.key],
  ])
);

/** Type definition — Data Review Dropdown Option. */
export type DataReviewDropdownOption = {
  id?: number | null;
  value?: string | null;
  label?: string | null;
};

function lookupDataReviewKey(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const direct = DATA_REVIEW_LABEL_TO_KEY.get(trimmed.toLowerCase());
  if (direct) return direct;

  const lower = trimmed.toLowerCase();
  for (const opt of DATA_REVIEW_EXTERNAL_OPTIONS) {
    const labelLower = opt.label.toLowerCase();
    if (lower === labelLower || lower.includes(labelLower) || labelLower.includes(lower)) {
      return opt.key;
    }
  }

  return null;
}

/** Resolve stored dropdown value / label text to the canonical Data Review internal key. */
export function normalizeDataReviewValue(
  raw: unknown,
  options: DataReviewDropdownOption[] = []
): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  if (DATA_REVIEW_ALL_KEYS.has(s)) return s;

  const fromCanonical = lookupDataReviewKey(s);
  if (fromCanonical) return fromCanonical;

  for (const opt of options) {
    const value = String(opt.value ?? "").trim();
    const label = String(opt.label ?? "").trim();
    const id = opt.id != null ? String(opt.id) : "";

    if (id && id === s && value) return value;
    if (value && value === s) return value;
    if (label && label.toLowerCase() === s.toLowerCase()) {
      return lookupDataReviewKey(label) ?? value ?? s;
    }
  }

  for (const opt of options) {
    const value = String(opt.value ?? "").trim();
    const label = String(opt.label ?? "").trim();
    if (!value && !label) continue;
    const matched = lookupDataReviewKey(label || value);
    if (matched) return matched;
    if (value && DATA_REVIEW_ALL_KEYS.has(value)) return value;
  }

  return s;
}

const DATA_REVIEW_EXTRA_LABELS: Record<string, string> = {
  internal_review: "Internal review",
  no_review_stated: "No review stated",
};

/** Human-readable label for stored Data Review slug / dropdown value. */
export function formatDataReviewDisplayLabel(
  raw: unknown,
  options: DataReviewDropdownOption[] = []
): string {
  const s = String(raw ?? "").trim();
  if (!s) return "-";

  const key = normalizeDataReviewValue(s, options);
  const fromCanonical = DATA_REVIEW_EXTERNAL_OPTIONS.find((opt) => opt.key === key);
  if (fromCanonical) return fromCanonical.label;

  const fromExtra = DATA_REVIEW_EXTRA_LABELS[key];
  if (fromExtra) return fromExtra;

  for (const opt of options) {
    const value = String(opt.value ?? "").trim();
    const label = String(opt.label ?? "").trim();
    if (value && (value === key || value === s) && label) return label;
    if (label && label.toLowerCase() === s.toLowerCase()) return label;
  }

  return s
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Exported function — is External Data Review Selection. */
export function isExternalDataReviewSelection(
  dataReview: unknown,
  options: DataReviewDropdownOption[] = []
): boolean {
  return requiredDataReviewerCount(dataReview, options) > 0;
}

/** Exported function — required Data Reviewer Count. */
export function requiredDataReviewerCount(
  dataReview: unknown,
  options: DataReviewDropdownOption[] = []
): number {
  const key = normalizeDataReviewValue(dataReview, options);
  const match = DATA_REVIEW_EXTERNAL_OPTIONS.find((opt) => opt.key === key);
  return match?.reviewerCount ?? 0;
}

/** Exported function — shows Data Reviewers Section. */
export function showsDataReviewersSection(
  dataReview: unknown,
  options: DataReviewDropdownOption[] = []
): boolean {
  return requiredDataReviewerCount(dataReview, options) > 0;
}

/** Exported function — data Reviewer Name Key. */
export function dataReviewerNameKey(index: number): string {
  return `dataReviewerName_${String(index).padStart(2, "0")}`;
}

/** Exported function — data Reviewer Email Key. */
export function dataReviewerEmailKey(index: number): string {
  return `dataReviewerEmail_${String(index).padStart(2, "0")}`;
}

/** Exported function — build Data Reviewer Groups. */
export function buildDataReviewerGroups(count: number): number[] {
  const n = Math.max(0, count);
  return Array.from({ length: n }, (_, i) => i + 1);
}

/** Exported function — is Data Reviewer Form Key. */
export function isDataReviewerFormKey(name: string): boolean {
  return /^dataReviewer(Name|Email)_\d{2}$/i.test(name);
}

/** Exported function — is Data Reviewer Name Template Field. */
export function isDataReviewerNameTemplateField(name: string): boolean {
  return (
    name === "dataReviewerName_01" ||
    name === "dataReviewerName" ||
    /^dataReviewerName_01$/i.test(name)
  );
}

/** Exported function — is Data Reviewer Email Template Field. */
export function isDataReviewerEmailTemplateField(name: string): boolean {
  return (
    name === "dataReviewerEmail_01" ||
    name === "dataReviewerEmail" ||
    /^dataReviewerEmail_01$/i.test(name)
  );
}

/** CMS anchor fields that drive the dynamic reviewer UI (must stay in form field order). */
export function isDataReviewerTemplateFieldName(name: string): boolean {
  return (
    isDataReviewerNameTemplateField(name) || isDataReviewerEmailTemplateField(name)
  );
}

/** Exported function — resolve Data Reviewer Field Variant. */
export function resolveDataReviewerFieldVariant(
  field: Pick<{ name?: string; type?: string; label?: string }, "name" | "type" | "label">
): "name" | "email" | null {
  const name = String(field.name ?? "");
  if (isDataReviewerNameTemplateField(name)) return "name";
  if (isDataReviewerEmailTemplateField(name)) return "email";

  const label = String(field.label ?? "").toLowerCase();
  if (field.type === "EMAIL" && label.includes("reviewer")) return "email";
  if (
    (field.type === "TEXT" || field.type === "EMAIL") &&
    label.includes("reviewer") &&
    label.includes("name")
  ) {
    return "name";
  }

  return null;
}

/** Exported function — is Data Reviewer Schema Field Name. */
export function isDataReviewerSchemaFieldName(name: string): boolean {
  return isDataReviewerTemplateFieldName(name) || isDataReviewerFormKey(name);
}

/** Exported function — is Valid Reviewer Email. */
export function isValidReviewerEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}
